import { fg } from '@atlaskit/platform-feature-flags/fg';

/**
 * Build a deterministic keyword JQL query of the form `textfields ~ "<query>*"`.
 *
 * This mirrors the NIN translation strategy used when a query goes from Quick
 * Find to the new Issue Navigator and is the FE counterpart to the backend's
 * `JIRA_NLQ_KEYWORD` suppression path introduced in xp-knowledge-discovery
 * PR #1611.
 *
 * The user query is sanitised before being interpolated so that the resulting
 * JQL string is always well-formed. In every case:
 *
 *  - Surrounding whitespace is trimmed.
 *  - Backslashes are escaped first (`\` → `\\`).
 *  - Double quotes are escaped so the surrounding quoted JQL string stays
 *    valid (`"` → `\"`).
 *
 * Wildcard handling is gated behind `kd_sain_jira_card_keyword_jql_escaping`
 * (JSAI-621).
 *
 * With the gate ON, the Lucene/JQL wildcard characters `*` and `?` are NOT
 * escaped. Escaping them made Lucene match a literal asterisk or question mark,
 * which is not indexed, so a query such as `fix * login` returned zero results.
 * When a user types a wildcard they mean it as a wildcard, so it is passed
 * through to the JQL untouched.
 *
 * With the gate ON, the trailing prefix-match `*` is also only appended when the
 * trimmed query does not already end in `"`, `*`, or `?`:
 *
 *  - Ending in `"` closes a quoted phrase. Lucene parses an appended `*` as a
 *    new term beginning with a wildcard, which is a syntax error.
 *  - Ending in `*` or `?` means the user already supplied a trailing wildcard,
 *    so appending ours would produce `**` or `?*`.
 *
 * Known limitation of the gate-ON behaviour: a wildcard that starts a term, such
 * as `*login` or the stray `*` in `fix * login`, is rejected by Lucene with "the
 * '*' and '?' are not allowed as first character in wildcard query". The query is
 * passed through verbatim to respect the user's query rather than rewritten,
 * so those inputs still surface that error.
 *
 * With the gate OFF, the legacy behaviour is preserved: `*` and `?` are escaped
 * so they are matched literally, and the trailing `*` is always appended.
 *
 * If the query is empty or whitespace-only the function returns `null` so the
 * caller can decide how to handle the no-op case (e.g. surface an error card).
 */
export const buildKeywordJql = (rawQuery: string): string | null => {
	const trimmed = rawQuery.trim();
	if (trimmed.length === 0) {
		return null;
	}

	// Escape backslashes first so the subsequent escapes don't get re-escaped,
	// then escape double quotes because they delimit the JQL string.
	const escapedBase = trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

	if (!fg('kd_sain_jira_card_keyword_jql_escaping')) {
		// Legacy behaviour: also escape the JQL wildcards `*` and `?` so user
		// input is matched literally, and always append the trailing `*`.
		const escapedLegacy = escapedBase.replace(/\*/g, '\\*').replace(/\?/g, '\\?');

		return `textfields ~ "${escapedLegacy}*"`;
	}

	// Wildcards are intentionally left alone so the user's `*` and `?` behave as
	// they typed them.
	//
	// The suffix check runs against the trimmed input rather than the escaped
	// output so a real trailing quote is detected instead of the escaped `\"`.
	const endsWithWildcardOrQuote = /["*?]$/.test(trimmed);
	const suffix = endsWithWildcardOrQuote ? '' : '*';

	return `textfields ~ "${escapedBase}${suffix}"`;
};
